/* eslint-disable prettier/prettier */
import './QuestionMedia.scss';

import * as React from 'react';
import { useState } from 'react';

import { translate } from '~l10n/client';
import cx from 'classnames';

import { SmallButton } from '~shared/components/buttons';
import { IconVideo } from '~shared/components/icons/standard/IconVideo/IconVideo';
import {
  EmbedVideoDialogProvider,
  useEmbedVideoModal,
} from '~teacher/components/QuizQuestion/EmbedVideoDialog/context/EmbedVideoDialogProvider';
import { useSelectQuestion } from '~teacher/components/QuizQuestion/helpers/useSelectQuestion';
import {
  QuestionImageEditing,
  QuestionImagePreviewer,
} from '~teacher/components/QuizQuestion/QuestionImage';
import { QuestionVideoPlayer } from '~teacher/components/QuizQuestion/QuestionVideoPlayer';

interface Props {
  questionId: string;
  onChangeImage?: (imageURL: string) => void;
}

export function QuestionMediaComponent({ questionId, onChangeImage }: Props) {
  const { questionImage, questionVideo, isEditing } = useSelectQuestion({
    questionId: questionId,
  });

  if (questionVideo?.url) {
    return (
      <QuestionVideoPlayer
        questionId={questionId}
        videoData={questionVideo}
        isEditing={isEditing}
      />
    );
  }

  if (questionImage?.url) {
    return isEditing ? (
      <QuestionImageEditing
        image={questionImage}
        onChangeImage={onChangeImage}
      />
    ) : (
      <div className="bg-white rounded-3 p-4">
        <QuestionImagePreviewer image={questionImage} />
      </div>
    );
  }

  if (!isEditing) {
    return null;
  }

  return <QuestionMediaAddMedia onChangeImage={onChangeImage} />;
}

interface QuestionMediaAddMediaProps {
  onChangeImage?: (imageURL: string) => void;
}

function QuestionMediaAddMedia({ onChangeImage }: QuestionMediaAddMediaProps) {
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const embedVideoModal = useEmbedVideoModal();

  const embedUploadContainerClasses = cx(
    'max-h-70 w-full bg-white rounded-3 embed-upload-container',
    {
      'px-10 py-2 grid items-center': !isUploadingImages,
      'max-w-70': isUploadingImages,
    }
  );

  const uploadButtonClasses = cx('grid w-full bg-white', {
    'py-4': !isUploadingImages,
  });

  return (
    <>
      <div className={embedUploadContainerClasses}>
        <div className="grid rounded-2 bg-grey-200" style={{ gap: '1px' }}>
          <div className={uploadButtonClasses}>
            <QuestionImageEditing
              onBeginUploadingImage={() => setIsUploadingImages(true)}
              onFinishUploadingImage={() => setIsUploadingImages(false)}
              onChangeImage={onChangeImage}
            />
          </div>
          {isUploadingImages ? null : (
            <div className="grid py-4 bg-white">
              <SmallButton appearance="ghost" onClick={embedVideoModal.open}>
                <div className="flex flex-col gap-1 items-center justify-center">
                  <IconVideo /> {translate('Embed Video')}
                </div>
              </SmallButton>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export function QuestionMedia(props: Props) {
  return (
    <EmbedVideoDialogProvider>
      <QuestionMediaComponent {...props} />
    </EmbedVideoDialogProvider>
  );
}
